import React, { useMemo, useState } from 'react';
import {
	Table as AntdTable,
	Col,
	Flex,
	Row,
} from 'antd';
import {
	Bar,
	BarChart,
	LabelList,
	Tooltip,
	XAxis,
	YAxis,
	LabelProps,
} from 'recharts';
import { CartesianViewBox } from 'recharts/types/util/types';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Moment } from 'moment';
import {
	DashboardTypes,
	DATE_AND_TIME,
	HireInvoiceItemStates,
	TimeZoneFormat,
	YearQuarterDashboardPeriods,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { round } from '@shared/utils/math';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import { Values } from '@shared/utils/objectEnums';
import { nowMoment, toMoment } from '@shared/utils/date';
import { standardSort } from '@shared/utils/standardSort';
import { capitalize } from '@shared/utils/string';
import type { GetOffHireDataResponse } from '@api/features/statistics/off-hire/getOffHireData';
import { useAuth } from '@client/lib/auth';
import { DashboardComponent } from '@client/screens/general/HomeScreen/helpers/dashboardInfo';
import { exportOffHireData, getOffHireData } from '@client/lib/api';
import Table from '@client/components/Table/Table';
import VesselDescription from '@client/components/VesselDescription';
import { Links } from '@client/utils/links';
import { formatDate } from '@client/utils/formatDate';
import StateTag from '@client/components/StateTag';
import { stringSetToSortedList } from '@client/utils/stringSetToSortedList';
import DatePicker from '@client/components/DatePicker';
import Button from '@client/components/Button';
import ChartWidget from '../components/ChartWidget';

const { UTC } = TimeZoneFormat;

const getDefaultStartDate = () => {
	const currentYear = nowMoment().year();

	return toMoment(`${currentYear}-01-01 0:00`);
};

const OffHireDashboard: DashboardComponent<YearQuarterDashboardPeriods> = () => {
	const [contractData, setContractData] = useState<GetOffHireDataResponse['data']['contract']>();
	const [contractTotalsData, setContractTotalsData] = useState<GetOffHireDataResponse['data']['contractTotals']>();
	const [vesselData, setVesselData] = useState<GetOffHireDataResponse['data']['vessel']>();
	const [vesselTotalsData, setVesselTotalsData] = useState<GetOffHireDataResponse['data']['vesselTotals']>();
	const [startDate, setStartDate] = useState(getDefaultStartDate());
	const [endDate, setEndDate] = useState(nowMoment().endOf('day'));
	const { userInfo } = useAuth();

	const personalVesselCacheKey = userInfo.filterVessels ?
		userInfo.relevantVessels.map((v) => v.id).sort((a, b) => a - b).join(',') :
		'all';

	const {
		isFetching: isLoading,
	} = useQuery(
		[DashboardTypes.OFF_HIRE, startDate, endDate, userInfo.filterVessels, personalVesselCacheKey],
		() => getOffHireData(startDate, endDate),
		{
			onSuccess: (d) => {
				setContractData(d.data.contract);
				setContractTotalsData(d.data.contractTotals);
				setVesselData(d.data.vessel);
				setVesselTotalsData(d.data.vesselTotals);
			},
			refetchOnWindowFocus: false,
		},
	);

	const exportDataMutatator = useMutation(
		() => exportOffHireData(startDate, endDate),
		{
			onSuccess: (data) => {
				const url = window.URL.createObjectURL(data);
				const link = document.createElement('a');
				link.href = url;

				link.download = 'off-hire-data.xlsx';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			}
		}
	);

	const groupedContractData: Array<{ vessel: { identifier: string }, totalCost: number }> = useMemo(() => {
		if (!contractData) {
			return [];
		}

		const groupedMap: Record<string, { vessel: { identifier: string }, totalCost: number }> = {};

		contractData.forEach(item => {
			const vesselName = item.vessel.identifier;

			if (groupedMap[vesselName]) {
				groupedMap[vesselName].totalCost += item.totalCost;
			} else {
				groupedMap[vesselName] = {
					vessel: { identifier: vesselName },
					totalCost: item.totalCost
				};
			}
		});

		return Object.values(groupedMap).sort((a, b) => b.totalCost - a.totalCost);
	}, [contractData]);

	const groupedVesselData: Array<{ vessel: { identifier: string }, totalDays: number }> = useMemo(() => {
		if (!vesselData) {
			return [];
		}

		const groupedMap: Record<string, { vessel: { identifier: string }, totalDays: number }> = {};

		vesselData.forEach(item => {
			const vesselName = item.vessel.identifier;

			if (groupedMap[vesselName]) {
				groupedMap[vesselName].totalDays += item.totalDays;
			} else {
				groupedMap[vesselName] = {
					vessel: { identifier: vesselName },
					totalDays: item.totalDays
				};
			}
		});

		return Object.values(groupedMap).sort((a, b) => b.totalDays - a.totalDays);
	}, [vesselData]);

	const groupedContractDataByCategory: Array<{ category: string, totalCost: number }> = useMemo(() => {
		if (!contractData) {
			return [];
		}

		const groupedMap: Record<string, { category: string, totalCost: number }> = {};

		contractData.forEach(item => {
			const category = item.offhireCategory ?? 'N/A';

			if (groupedMap[category]) {
				groupedMap[category].totalCost += item.totalCost;
			} else {
				groupedMap[category] = {
					category,
					totalCost: item.totalCost
				};
			}
		});

		return Object.values(groupedMap).sort((a, b) => b.totalCost - a.totalCost);
	}, [contractData]);

	const groupedVesselDataByCategory: Array<{ category: string, totalDays: number }> = useMemo(() => {
		if (!vesselData) {
			return [];
		}

		const groupedMap: Record<string, { category: string, totalDays: number }> = {};

		vesselData.forEach(item => {
			const category = item.offHireCategory ?? 'N/A';

			if (groupedMap[category]) {
				groupedMap[category].totalDays += item.totalDays;
			} else {
				groupedMap[category] = {
					category,
					totalDays: item.totalDays
				};
			}
		});

		return Object.values(groupedMap).sort((a, b) => b.totalDays - a.totalDays);
	}, [vesselData]);

	const filterMaps = useMemo(() => {
		const vessels = new Set<string>();
		const contracts = new Set<string>();
		const types = new Set<string>();
		const contractCategories = new Set<string>();
		const invoiceStatuses = new Set<Values<typeof HireInvoiceItemStates>>();

		contractData?.forEach((item) => {
			vessels.add(item.vessel.identifier);
			if (item.contract){
				contracts.add(item.contract.identifier);
			}

			if (item.offhireCategory) {
				contractCategories.add(item.offhireCategory);
			}

			if (item.invoiceStatus) {
				invoiceStatuses.add(item.invoiceStatus);
			}
		});

		vesselData?.forEach((item) => item.offHireCategory && types.add(item.offHireCategory));

		return {
			vessels: stringSetToSortedList<string>(vessels),
			contracts: stringSetToSortedList<string>(contracts),
			types: stringSetToSortedList<string>(types),
			contractCategories: stringSetToSortedList<string>(contractCategories),
			invoiceStatuses: stringSetToSortedList<Values<typeof HireInvoiceItemStates>>(invoiceStatuses),
		};
	}, [contractData, vesselData]);

	return (
		<Row gutter={[16, 16]}>
			<Col xl={24}>
				<Flex style={{ width: 'fit-content' }} align={'flex-end'} gap={10}>
					<Flex vertical gap={5}>
						<span>From</span>
						<DatePicker 
							defaultValue={startDate}
							onChange={(date) => setStartDate((date as Moment).startOf('day'))}
						/>
					</Flex>
					<Flex vertical gap={5}>
						<span>To</span>
						<DatePicker 
							minDate={startDate}
							defaultValue={endDate}
							onChange={(date) => setEndDate((date as Moment).endOf('day'))}
						/>
					</Flex>
					<Button 
						type='primary'
						onClick={() => {
							exportDataMutatator.mutate();
						}}
						loading={exportDataMutatator.isLoading}
					>
						Export to Excel
					</Button>
				</Flex>
			</Col>
			<Col xs={24} xl={12}>
				<ChartWidget
					height={200}
					header="Total Off-hire Cost By Vessel"
					staticData={groupedContractData}
					externalLoading={isLoading}
					renderChart={(chartData) => {
						const renderCustomizedLabel = (props: LabelProps) => {
							const { x, y, width, value } = props;

							const viewBox = props.viewBox as CartesianViewBox;

							if (
								value == null ||
								viewBox == null ||
								viewBox?.height == null
							) {
								return (<text />);
							}

							return (
								<g>
									<text
										x={Number(x) + Number(width) + 10}
										y={Number(y) + 5 + (Number(viewBox.height) / 2)}
										fill="#275171"
										textAnchor="start"
										style={{ fontWeight: 'bold', fontSize: 16 }}
										dominantBaseline="end"
									>
										{
											Number(value) < 1_000_000 ?
												`${formatCurrency(
													Math.round(Number(value) / 1000), 
													userInfo.baseCurrency
												)}K` :
												`${formatCurrency(
													round(Number(value) / 1_000_000, 2), 
													userInfo.baseCurrency)
												}M`
										}
									</text>
								</g>
							);
						};

						return (
							<BarChart
								width={600}
								height={200}
								data={chartData}
								layout="vertical"
								margin={{ top: 5, right: 45, left: 20, bottom: 5 }}
							>
								<XAxis type="number" />
								<YAxis type="category" dataKey={(d) => d.vessel.identifier} />
								<Tooltip
									formatter={(value: number) => formatCurrency(value, userInfo.baseCurrency)}
									cursor={false}
								/>
								<Bar name="Total off hire cost" dataKey="totalCost" fill="#275171">
									<LabelList dataKey="totalCost" content={renderCustomizedLabel} />
								</Bar>
							</BarChart>
						);
					}}
				/>
			</Col>
			<Col xs={24} xl={12}>
				<ChartWidget
					header="Total Off-hire Days By Vessel"
					staticData={groupedVesselData}
					externalLoading={isLoading}
					height={200}
					renderChart={(chartData) => {
						const renderCustomizedLabel = (props: LabelProps) => {
							const { x, y, width, value } = props;
							const viewBox = props.viewBox as CartesianViewBox;

							if (value == null || viewBox == null || viewBox?.height == null) {
								return (<text />);
							}

							return (
								<g>
									<text
										x={Number(x) + Number(width) + 10}
										y={Number(y) + 5 + (Number(viewBox.height) / 2)}
										fill="#275171"
										textAnchor="start"
										style={{ fontWeight: 'bold', fontSize: 16 }}
										dominantBaseline="end"
									>
										{`${formatAndRoundNumber(Number(value), 2)} days`}
									</text>
								</g>
							);
						};

						return (
							<BarChart
								width={550}
								height={200}
								data={chartData}
								layout="vertical"
								margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
							>
								<XAxis type="number" />
								<YAxis type="category" dataKey={(d) => d.vessel.identifier} />
								<Tooltip formatter={(value: number) => round(value, 3)} />
								<Bar name="Total off hire days" dataKey="totalDays" fill="#275171">
									<LabelList dataKey="totalDays" content={renderCustomizedLabel} />
								</Bar>
							</BarChart>
						);
					}}
				/>
			</Col>
			<Col xs={24} xl={12}>
				<ChartWidget
					height={200}
					header="Total Off-hire Cost By Category"
					staticData={groupedContractDataByCategory}
					externalLoading={isLoading}
					renderChart={(chartData) => {
						const renderCustomizedLabel = (props: LabelProps) => {
							const { x, y, width, value } = props;

							const viewBox = props.viewBox as CartesianViewBox;

							if (
								value == null ||
								viewBox == null ||
								viewBox?.height == null
							) {
								return (<text />);
							}

							return (
								<g>
									<text
										x={Number(x) + Number(width) + 10}
										y={Number(y) + 5 + (Number(viewBox.height) / 2)}
										fill="#275171"
										textAnchor="start"
										style={{ fontWeight: 'bold', fontSize: 16 }}
										dominantBaseline="end"
									>
										{
											Number(value) < 1_000_000 ?
												`${formatCurrency(
													Math.round(Number(value) / 1000), 
													userInfo.baseCurrency
												)}K` :
												`${formatCurrency(
													round(Number(value) / 1_000_000, 2), 
													userInfo.baseCurrency)
												}M`
										}
									</text>
								</g>
							);
						};

						return (
							<BarChart
								width={600}
								height={200}
								data={chartData}
								layout="vertical"
								margin={{ top: 5, right: 45, left: 20, bottom: 5 }}
							>
								<XAxis type="number" />
								<YAxis type="category" dataKey={(d) => d.category} />
								<Tooltip
									formatter={(value: number) => formatCurrency(value, userInfo.baseCurrency)}
									cursor={false}
								/>
								<Bar name="Total off hire cost" dataKey="totalCost" fill="#275171">
									<LabelList dataKey="totalCost" content={renderCustomizedLabel} />
								</Bar>
							</BarChart>
						);
					}}
				/>
			</Col>
			<Col xs={24} xl={12}>
				<ChartWidget
					header="Total Off-hire Days By Category"
					staticData={groupedVesselDataByCategory}
					externalLoading={isLoading}
					height={200}
					renderChart={(chartData) => {
						const renderCustomizedLabel = (props: LabelProps) => {
							const { x, y, width, value } = props;
							const viewBox = props.viewBox as CartesianViewBox;

							if (value == null || viewBox == null || viewBox?.height == null) {
								return (<text />);
							}

							return (
								<g>
									<text
										x={Number(x) + Number(width) + 10}
										y={Number(y) + 5 + (Number(viewBox.height) / 2)}
										fill="#275171"
										textAnchor="start"
										style={{ fontWeight: 'bold', fontSize: 16 }}
										dominantBaseline="end"
									>
										{`${formatAndRoundNumber(Number(value), 2)} days`}
									</text>
								</g>
							);
						};

						return (
							<BarChart
								width={550}
								height={200}
								data={chartData}
								layout="vertical"
								margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
							>
								<XAxis type="number" />
								<YAxis type="category" dataKey={(d) => d.category} />
								<Tooltip formatter={(value: number) => round(value, 3)} />
								<Bar name="Total off hire days" dataKey="totalDays" fill="#275171">
									<LabelList dataKey="totalDays" content={renderCustomizedLabel} />
								</Bar>
							</BarChart>
						);
					}}
				/>
			</Col>
			<Col span={24}>
				<Table
					size='small'
					dataSource={contractData}	
					title={() => 'Off hire data allocated to contracts'}
					summary={() => contractTotalsData && (
						<AntdTable.Summary.Row>
							<AntdTable.Summary.Cell index={0}>
								<b>Total</b>
							</AntdTable.Summary.Cell>
							<AntdTable.Summary.Cell index={1}/>
							<AntdTable.Summary.Cell index={2}/>
							<AntdTable.Summary.Cell index={3}/>
							<AntdTable.Summary.Cell index={4}/>
							<AntdTable.Summary.Cell index={5}/>
							<AntdTable.Summary.Cell index={6}/>
							<AntdTable.Summary.Cell index={7}>
								<b>
									{round(contractTotalsData.totalDays, 3)}
								</b>
							</AntdTable.Summary.Cell>
						</AntdTable.Summary.Row>
					)}
					pagination={{
						hideOnSinglePage: true,
						pageSize: 30,
					}}
					columns={[
						{
							title: 'Vessel',
							dataIndex: 'name',
							render: (name: string, record) => (
								<VesselDescription 
									name={name} 
									vesselId={record.vesselId} 
								/>
							),
							filters: filterMaps.vessels.map((vessel) => ({
								text: vessel,
								value: vessel,
							})),
							filterMultiple: true,
							key: 'vessel',
							onFilter: (
								value, 
								record
							) => value === record.name
						}, 
						{
							title: 'Date from',
							dataIndex: 'dateFrom',	
							render: (d) => formatDate(d, DATE_AND_TIME, true, UTC),
						}, 
						{
							title: 'Date to',
							dataIndex: 'dateTo',
							render: (d) => formatDate(d, DATE_AND_TIME, true, UTC),
						},
						{
							title: 'Off-hire category',
							dataIndex: 'offhireCategory',
							render: (category: string) => category ?? '',
							filters: filterMaps.contractCategories.map((category) => ({
								text: category,
								value: category,
							})),
							filterMultiple: true,
							key: 'offhireCategory',
							onFilter: (value, record) => record.offhireCategory === value,
						},
						{
							title: 'Invoice status',
							dataIndex: 'invoiceStatus',
							render: (status: Values<typeof HireInvoiceItemStates>) => status ? (
								<StateTag state={status} />
							) : '',
							filters: filterMaps.invoiceStatuses.map((status) => ({
								text: capitalize(status),
								value: status,
							})),
							filterMultiple: true,
							key: 'invoiceStatus',
							onFilter: (value, record) => record.invoiceStatus === value,
						},
						{
							title: 'Contract ID',
							dataIndex: 'contract',
							render: (contract: { id: number; identifier: string }) => contract ? (
								<Link to={Links.Voyage.get(contract.id)}>{contract.identifier}</Link>
							) : '',		
							filters: filterMaps.contracts.map((contract) => ({
								text: contract,
								value: contract,
							})),
							filterMultiple: true,
							key: 'contract',
							onFilter: (value, record) => record.contract?.identifier === value,
						},
						{
							title: 'Total cost',
							dataIndex: 'totalCost',
							render: (v, row) => v != null ? formatCurrency(v, row.currency) : '',
							sorter: standardSort('totalCost'),
						},
						{
							title: 'Total days',
							dataIndex: 'totalDays',
							render: (v) => v != null ? round(v, 3) : '',
							sorter: standardSort('totalDays')
						}
					]}
				/>
			</Col>
			<Col span={24}>
				<Table
					size='small'
					title={() => 'Vessel off hires'}
					dataSource={vesselData}	
					summary={() => vesselTotalsData && (
						<AntdTable.Summary.Row>
							<AntdTable.Summary.Cell index={0}>
								<b>Total</b>
							</AntdTable.Summary.Cell>
							<AntdTable.Summary.Cell index={1}/>
							<AntdTable.Summary.Cell index={2}/>
							<AntdTable.Summary.Cell index={3}/>
							<AntdTable.Summary.Cell index={7}>
								<b>
									{round(vesselTotalsData.totalDays, 3)}
								</b>
							</AntdTable.Summary.Cell>
						</AntdTable.Summary.Row>
					)}
					pagination={{
						hideOnSinglePage: true,	
						pageSize: 30,
					}}
					columns={[
						{
							title: 'Vessel',
							dataIndex: 'vessel',
							render: (vessel: { id: number; identifier: string; flag: string } | string) => (
								typeof vessel === 'object' ? (
									<VesselDescription 
										name={vessel.identifier} 
										vesselId={vessel.id} 
										flag={vessel.flag} 
									/>
								) : vessel),
							filters: filterMaps.vessels.map((vessel) => ({
								text: vessel,
								value: vessel,
							})),
							filterMultiple: true,
							key: 'vessel',
							onFilter: (
								value, 
								record
							) => typeof record.vessel === 'object' && record.vessel?.identifier === value,
						}, 
						{
							title: 'Off hire category',
							dataIndex: 'offHireCategory',
							render: (cat) => cat ?? '-',
							filters: filterMaps.types.map((type) => ({
								text: type,
								value: type,
							})),
							filterMultiple: true,
							key: 'offHireCategory',
							onFilter: (value, record) => record.offHireCategory === value
						},
						{
							title: 'Date from',
							dataIndex: 'dateFrom',	
							render: (d) => formatDate(d, DATE_AND_TIME, true, UTC),
						}, 
						{
							title: 'Date to',
							dataIndex: 'dateTo',
							render: (d) => formatDate(d, DATE_AND_TIME, true, UTC),
						},
						{
							title: 'Total days',
							dataIndex: 'totalDays',
							render: (v) => v != null ? round(v, 3) : '',
							sorter: standardSort('totalDays')
						}
					]}
				/>
			</Col>
		</Row>
	);
};

export default OffHireDashboard;
